import { App as AntdApp, Card, Flex } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentSpinner } from 'src/components'
import {
  WorkspacePageShell,
  useWorkspaceRoute
} from 'src/features/workspaces/pages/WorkspacePageShell'
import { useHasWorkspacePermissions } from 'src/hooks'
import { WorkspacePermissionCode } from 'src/utils'

import {
  OrgBrowseView,
  OrgChartViewCard,
  OrgDetailDrawer,
  OrgFormDrawer,
  OrgKpiChips,
  OrgToolbar,
  OrgTreeView,
  type OrgFormMode
} from '../components'
import { useOrganizationTeams, useTeamMutations } from '../hooks'
import type { OrgNodeKind, OrgTypeFilter, OrgUnit, OrgView } from '../types'
import { apiTypeToKind, childKindOf } from '../utils'

export const OrganizationPage = () => {
  const { t } = useTranslation()
  const { workspaceId, idValid: valid } = useWorkspaceRoute()
  const { modal, message } = AntdApp.useApp()

  const { teams, isLoading } = useOrganizationTeams(workspaceId, valid)
  const { destroy } = useTeamMutations(workspaceId)

  // The structure is the workspace's people, so editing it follows
  // `users.manage` — the same code that gates a member's teams and groups.
  const mayManage = useHasWorkspacePermissions(
    WorkspacePermissionCode.USERS_MANAGE
  )

  const [view, setView] = useState<OrgView>('chart')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<OrgTypeFilter>('all')
  const [selected, setSelected] = useState<OrgUnit | null>(null)
  const [formMode, setFormMode] = useState<OrgFormMode | null>(null)

  // `selected` is a click-time snapshot. Re-resolve it against the freshest
  // teams list so the detail drawer (header, lead card, headcount) reflects
  // edits made via the nested form drawer the moment that drawer closes.
  const selectedUnit = useMemo(
    () =>
      selected ? (teams.find((u) => u.id === selected.id) ?? selected) : null,
    [selected, teams]
  )

  const counts = useMemo(() => {
    let divisions = 0
    let departments = 0
    let teamCount = 0
    let assigned = 0
    for (const u of teams) {
      const kind = apiTypeToKind(u.type)
      if (kind === 'division') divisions += 1
      else if (kind === 'department') departments += 1
      else teamCount += 1
      assigned += u.users_count
    }
    return { divisions, departments, teamCount, assigned }
  }, [teams])

  const openCreate = (defaultType: OrgNodeKind, parent?: OrgUnit | null) => {
    setFormMode({ kind: 'create', defaultType, parent: parent ?? null })
  }

  const openEdit = (unit: OrgUnit) => {
    setFormMode({ kind: 'edit', unit })
  }

  const handleAddChild = (unit: OrgUnit) => {
    const childKind = childKindOf(apiTypeToKind(unit.type))
    if (!childKind) return
    openCreate(childKind, unit)
  }

  const handleDelete = (unit: OrgUnit) => {
    modal.confirm({
      title: t('organization.delete.title'),
      content: t('organization.delete.content', { name: unit.name }),
      okText: t('organization.delete.confirm'),
      okButtonProps: { danger: true },
      cancelText: t('organization.delete.cancel'),
      onOk: async () => {
        await destroy.mutateAsync({ path: { id: unit.id } })
      }
    })
  }

  const handleExport = () => {
    message.info(t('organization.notApiBacked.exportToast'))
  }

  if (!valid) {
    return null
  }

  const formDrawer = (
    <OrgFormDrawer
      workspaceId={workspaceId}
      open={formMode !== null}
      mode={formMode}
      units={teams}
      onClose={() => setFormMode(null)}
    />
  )

  return (
    <WorkspacePageShell
      sectionTitle={t('organization.pageTitle')}
      sectionDescription={t('organization.pageSubtitle')}
      titleExtra={
        <OrgKpiChips
          divisions={counts.divisions}
          departments={counts.departments}
          teams={counts.teamCount}
          membersAssigned={counts.assigned}
        />
      }
    >
      {isLoading ? (
        <ContentSpinner minHeight={400} />
      ) : (
        <Flex vertical gap={14}>
          <Card styles={{ body: { padding: 0 } }}>
            <OrgToolbar
              view={view}
              onViewChange={setView}
              search={search}
              onSearchChange={setSearch}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              onExport={handleExport}
              onCreate={mayManage ? () => openCreate('division') : undefined}
            />

            {view === 'chart' ? (
              <OrgChartViewCard
                workspaceId={workspaceId}
                units={teams}
                onSelect={setSelected}
              />
            ) : null}

            {view === 'tree' ? (
              <OrgTreeView
                units={teams}
                search={search}
                typeFilter={typeFilter}
                onSelect={setSelected}
                onEdit={mayManage ? openEdit : undefined}
                onAddChild={mayManage ? handleAddChild : undefined}
                onDelete={mayManage ? handleDelete : undefined}
              />
            ) : null}

            {view === 'browse' ? (
              <OrgBrowseView
                units={teams}
                search={search}
                typeFilter={typeFilter}
                onSelect={setSelected}
                onEdit={mayManage ? openEdit : undefined}
                onAddChild={mayManage ? handleAddChild : undefined}
                onDelete={mayManage ? handleDelete : undefined}
              />
            ) : null}
          </Card>

          <OrgDetailDrawer
            workspaceId={workspaceId}
            unit={selectedUnit}
            units={teams}
            onClose={() => setSelected(null)}
            onEdit={mayManage ? openEdit : undefined}
            onAddChild={mayManage ? handleAddChild : undefined}
            nestedDrawer={mayManage ? formDrawer : null}
          />

          {selected || !mayManage ? null : formDrawer}
        </Flex>
      )}
    </WorkspacePageShell>
  )
}
